'use client';

import posthog from 'posthog-js';
import { PostHogProvider as PHProvider, usePostHog } from 'posthog-js/react';
import { useEffect, Suspense } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';
import { NEXT_PROD } from '@/shared/envs';

const POSTHOG_KEY = process.env.NEXT_PUBLIC_POSTHOG_KEY;
const POSTHOG_HOST = process.env.NEXT_PUBLIC_POSTHOG_HOST || 'https://us.i.posthog.com';
const IS_PROD = NEXT_PROD === 'true';

// Initialize PostHog on client side only
if (typeof window !== 'undefined' && POSTHOG_KEY) {
  posthog.init(POSTHOG_KEY, {
    api_host: POSTHOG_HOST,
    person_profiles: 'identified_only',
    capture_pageview: false, // We capture pageviews manually for better control
    capture_pageleave: true, // Required for scroll heatmaps
    autocapture: true, // Required for click heatmaps
    enable_heatmaps: true, // Enable heatmap data collection
    persistence: 'localStorage+cookie',
    loaded: (posthog) => {
      if (!IS_PROD) {
        console.log('PostHog loaded successfully');
      }
    },
  });
}

// Component that captures pageviews on route changes
function PostHogPageView() {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const posthogClient = usePostHog();

  useEffect(() => {
    if (pathname && posthogClient) {
      let url = window.origin + pathname;
      if (searchParams && searchParams.toString()) {
        url = url + '?' + searchParams.toString();
      }
      posthogClient.capture('$pageview', {
        $current_url: url,
      });
    }
  }, [pathname, searchParams, posthogClient]);

  return null;
}

// Wrapper with Suspense for searchParams
function SuspendedPageView() {
  return (
    <Suspense fallback={null}>
      <PostHogPageView />
    </Suspense>
  );
}

export function PostHogProvider({ children }: { children: React.ReactNode }) {
  // Don't render PostHog if key is not configured
  if (!POSTHOG_KEY) {
    if (typeof window !== 'undefined' && !IS_PROD) {
      console.log('PostHog not initialized: NEXT_PUBLIC_POSTHOG_KEY not set');
    }
    return <>{children}</>;
  }

  return (
    <PHProvider client={posthog}>
      <SuspendedPageView />
      {children}
    </PHProvider>
  );
}

export default PostHogProvider;
